import React, { useState, useEffect, useMemo } from 'react'
import { Button, Field, Groupper, Header, Input, Message, Table, Icon, useFrontier, Checkbox, Dropdown, Modal } from "react-frontier2";
import { Coupon, CouponLimitType, CouponType, EventDate, SetLoading } from '@arema/components/Classes';
import { useParams } from "react-router-dom";
import { addCommas, bindClick, bindFormChange, fromUnix, getNow, getNowLocal, uniqueArray } from '@arema/components/Util';
import { CreatedGroupper, DeletedBanner, ErrorHeader, NotFound, SearchDateModal, SearchEventModal, UserLog } from '../../components';
import { useTitle } from '@arema/components/Hooks';
import { useUser } from '../../AdminHooks';
import { EventSectionProps } from '../../AdminClasses';
import { useTranslation } from 'react-i18next';
import Validator from "@arema/components/Validator";
import API from "./../../API";
import UserAccess from '../../UserAccess';

const COUPON_TYPES = [
	{ id: CouponType.AMOUNT, 			name: 'Cantidad' },
	{ id: CouponType.PERCENT, 			name: 'Porcentaje' },
	{ id: CouponType.X_PER_X, 			name: '? x ?' },
	{ id: CouponType.X_PER_MONEY, 	name: '? x $' }
];

const COUPON_LIMIT_TYPES = [
	{ id: -1,								name: 'Sin limite' },
	{ id: CouponLimitType.TICKETS, 	name: 'Boletos' },
	{ id: CouponLimitType.ORDERS,		name: 'Ordenes' },
]

interface CouponData{
	coupon_id: number
	coupon_code: string, 
	description: string,
	type_id: number,
	param1: number,
	param2: number,
	limit_type: CouponLimitType,
	limit_amount: number,
	all_event: boolean,
	all_sections: boolean,
	active: boolean,
	date_start: number,
	date_end: number,
	deleted?: boolean,
	user_creator?: number,
	user_creator_name?: string,
	date_created?: number,
	user_deleted?: number,
	user_deleted_name?: string,
	date_deleted?: number,
}

interface CouponSection{
	section_id: number,
	venue_id: number,
	venue_name: string,
	section_name: string,
}

interface CouponPrice{
	price_id: number,
	price_name: string,
	date_id: number,
	date: number,
	section_id: number,
	section_name: string,
	cost: number,
	commission: number,
	venue_id: number,
	venue_name: string,
}

enum Modals{
	SELECT_DATE = 1,
	SELECT_SECTION = 2,
	DELETE = 99,
}

var CouponEdit = (props: EventSectionProps)=>{
	var params = useParams();
	var { t } = useTranslation();
	var { setTitle } = useTitle();
	var { user, hasAccess } = useUser();
	var { Toast } = useFrontier();
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [selectedDates, setSelectedDates] = useState<EventDate[]>([]);
	var [selectedPrices, setSelectedPrices] = useState<CouponPrice[]>([]);
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [eventSections, setEventSections] = useState<CouponSection[]>(null);
	var [selectedSection, setSelectedSection] = useState<number>(null);
	var [sectionPrices, setSectionPrices] = useState<{
		section_id: number,
		prices: CouponPrice[]
	}[]>(null);
	var [coupon, setCoupon] = useState<CouponData>(null)
	var [loadError, setLoadError] = useState<string>(null);
	var [created, setCreated] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string>(null);
	var IS_CREATE = params.coupon_id === 'create';
	var coupon_id = !IS_CREATE && !Number.isNaN(parseInt(params.coupon_id)) ? parseInt(params.coupon_id) : null;

	var onDateSelected = (date: EventDate): boolean=>{
		var ix = selectedDates.findIndex(a=>a.date_id===date.date_id);
		if(ix==-1){
			setSelectedDates([...selectedDates, date]);
		}else{
			setSelectedDates(selectedDates.filter(a=>a.date_id!==date.date_id));
		}
		return false;
	}

	var onPriceSelected = (price: CouponPrice)=>{
		return ()=>{
			var ix = selectedPrices.findIndex(a=>a.price_id===price.price_id);
			if(ix==-1){
				setSelectedPrices([...selectedPrices, price]);
			}else{
				setSelectedPrices(selectedPrices.filter(a=>a.price_id!==price.price_id));
			}
		}
	}

	useEffect(()=>{
		if(!IS_CREATE && coupon_id!==null){
			if(!coupon || coupon?.coupon_id!==coupon_id){
				loadData(coupon_id);
			}
		}else{
			setTitle('Crear cupón');
			if(coupon) return;
			setCoupon({
				coupon_id: null,
				coupon_code: '',
				description: '',
				type_id: null,
				param1: null,
				param2: null,
				limit_type: null,
				limit_amount: 0,
				all_event: false,
				all_sections: true,
				active: true,
				date_start: null,
				date_end: null,
			})
		}
	}, [coupon_id]);

	useEffect(()=>{
		if(shownModal===Modals.SELECT_SECTION){
			setSelectedSection(null);
			setEventSections(null);
			API.getEventSections(props.event.event_id).then(res=>{
				if(res.error){
					Toast.error(res.message);
					return setShownModal(null);
				}
				setEventSections(res.data);
			}).catch(err=>{
				Toast.error(t('common.unexpected_code', { code: 'LCL-EVCPGTSC-1' }));
				return setShownModal(null);
			})
		}
	}, [shownModal]);

	useEffect(()=>{
		if(!selectedSection) return;
		var ix = sectionPrices ? sectionPrices.findIndex(a=>a.section_id===selectedSection) : -1;
		if(ix==-1){
			API.getEventSectionPrices(props.event.event_id, selectedSection).then(res=>{
				if(res.error){
					Toast.error(res.message);
					return setSelectedSection(null);
				}
				var pr = sectionPrices ? [...sectionPrices] : [];
				pr.push({
					section_id: selectedSection,
					prices: res.data
				});
				setSectionPrices(pr);
			}).catch(err=>{
				Toast.error(t('common.unexpected_code', { code: 'LCL-EVCPGTPR-1' }));
				return setShownModal(null);
			});
		}
	}, [selectedSection, sectionPrices]);

	var is_x = useMemo(()=>{
		if(!coupon?.type_id) return false;
		return [CouponType.X_PER_MONEY, CouponType.X_PER_X].includes(coupon.type_id);
	}, [coupon?.type_id]);

	var loadData = async (coupon_id: number)=>{
		try{
			var res = await API.getCoupon(coupon_id);
			if(res.error) return setLoadError(res.message);
			setCoupon({
				...res.data,
				active: !!res.data.active,
			});
			if(res.data.dates){
				setSelectedDates(res.data.dates);
			}
			if(res.data.prices){
				setSelectedPrices(res.data.prices);
			}
			setTitle(`Cupón ${res.data.coupon_code}`);
		}catch(e){
			setLoadError('Hubo un error cargando el cupón (LCL-CPNVIW-1)');
		}
	}

	var removeDate = (date_id: number)=>{
		return ()=>{
			setSelectedDates(selectedDates.filter(sev=>sev.date_id !== date_id));
		}
	}

	var removePrice = (price_id: number)=>{
		return ()=>{
			setSelectedPrices(selectedPrices.filter(a=>a.price_id!==price_id));
		}
	}

	var submit = async (setLoading: (b: boolean)=>void)=>{
		var { valid, prompts } = Validator(coupon, {
			coupon_code: [
				{ rule: 'length', params: [3, 32], label: 'Código' }, 
				{ rule: /^[A-Za-z0-9][A-Za-z0-9]*$/, prompt: 'El código debe de empezar con una letra y solo debe de incluir caracteres alfanuméricos', skipEmpty: true }
			],
			description: [{ rule: 'length', params: [3, 128], label: 'Descripción' }],
			type_id: [{ rule: 'empty', prompt: 'Favor de seleccionar un tipo de cupón' }],
			param1: [{ rule: 'number', prompt: `Favor de ingresar la${is_x ? ' primera' : ''} cantidad del cupón` }],
			param2: is_x ? [{ rule: 'number', prompt: 'Favor de ingresar la segunda cantidad del cupón' }] : [],
			limit_amount: coupon.limit_type!==null ? [{ rule: 'number', prompt: 'La cantidad de usos no es válida' }] : []
		});
		setErrorPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		var saveCoupon: Coupon = {
			coupon_code: coupon.coupon_code,
			event_id: props.event.event_id,
			description: coupon.description,
			type_id: coupon.type_id,
			param1: coupon.param1,
			param2: coupon.param2,
			limit_type: coupon.limit_type,
			limit_amount: coupon.limit_amount,
			all_event: coupon.all_event,
			all_sections: coupon.all_sections,
			active: coupon.active,
			date_start: coupon.date_start ? fromUnix(coupon.date_start).startOf('day').toSeconds() : null,
			date_end: coupon.date_end ? fromUnix(coupon.date_end).endOf('day').toSeconds() : null,
		}
		if(IS_CREATE){
			API.createCoupon(saveCoupon, selectedDates.map(d=>d.date_id), selectedPrices.map(a=>a.price_id)).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				Toast.success('Se creó el cupón');
				setCreated(true);
				setCoupon({
					...coupon,
					coupon_id: res.data.coupon_id,
					date_created: getNowLocal().toSeconds(),
					user_creator: user.user_id,
					user_creator_name: user.name,
				})
			}).catch(err=>{
				setErrorPrompts([t('common.unexpected_code', { code: 'LCL-CPNVIW-2' })]);
			}).finally(()=>{
				setLoading(false);
			});
		}else{
			API.editCoupon(coupon_id, saveCoupon, selectedDates.map(d=>d.date_id), selectedPrices.map(a=>a.price_id)).then(res=>{
				if(res.error) return setErrorPrompts([res.message]);
				Toast.success('Se ha editado el cupón.');
			}).catch(err=>{
				setErrorPrompts([t('common.unexpected_code', { code: 'LCL-CPNVIW-3' })]);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	var deleteCoupon = (setLoading: SetLoading)=>{
		if(!hasAccess(UserAccess.CATALOGS.COUPONS_DELETE)) return;
		setLoading(true);
		setModalPrompts(null);
		API.deleteCoupon(coupon.coupon_id).then(res=>{
			if(res.error) return setModalPrompts(res.message);
			setCoupon({
				...coupon,
				deleted: true,
				user_deleted: user.user_id,
				user_deleted_name: user.name,
				date_deleted: getNowLocal().toSeconds(),
			});
			setShownModal(null);
			Toast.success('El cupón ha sido eliminado');
		}).catch(err=>{
			Toast.error(t('common.unexpected_code', { code: 'LCL-CPNVIW-4' }));
		}).finally(()=>{
			setLoading(false);
		})
	}

	var now = useMemo(()=>{
		return getNowLocal();
	}, []);

	var selected_section = useMemo(()=>{
		if(!selectedSection || !eventSections) return null;
		return eventSections.find(a=>a.section_id===selectedSection);
	}, [eventSections, selectedSection]);

	var selected_prices = useMemo(()=>{
		if(!selectedPrices) return [];
		var selprices = selectedPrices

		var prices : {
			date_id: number,
			date: number,
			venue_name: string,
			prices: CouponPrice[]
		}[] = [];

		for(var i of uniqueArray(selprices.map(a=>a.date_id))){
			var dpr = selprices.filter(a=>a.date_id===i);
			if(dpr.length==0) continue;
			prices.push({
				date_id: dpr[0].date_id,
				date: dpr[0].date,
				venue_name: dpr[0].venue_name,
				prices: dpr,
			});
		}
		return prices;
	}, [selectedPrices, now]);

	var section_prices = useMemo(()=>{
		if(!sectionPrices || !selectedSection) return null;
		var pr = sectionPrices.find(a=>a.section_id===selectedSection);
		if(!pr || !pr.prices) return null;
		if(pr.prices.length==0) return [];
		
		var prices : {
			date_id: number,
			date: number,
			venue_name: string,
			prices: CouponPrice[]
		}[] = [];

		for(var i of uniqueArray(pr.prices.map(a=>a.date_id))){
			var dpr = pr.prices.filter(a=>a.date_id===i);
			if(dpr.length==0) continue;
			prices.push({
				date_id: dpr[0].date_id,
				date: dpr[0].date,
				venue_name: dpr[0].venue_name,
				prices: dpr,
			});
		}

		return prices;
	}, [sectionPrices, selectedSection]);

	if(!IS_CREATE && coupon_id===null) return <NotFound />
	if(loadError) return <ErrorHeader error={loadError} />
	if(!coupon) return <Header loading text='Cargando cupón' />
	if(IS_CREATE && created){
		return <CreatedGroupper
			title='Nuevo cupón'
			text='Cupón creado'
			subtext={`Se ha creado el cupón #${coupon.coupon_id}`}
			buttonText='Ver cupón'
			link={`/events/${props.event.event_id}/promotions/coupon/${coupon.coupon_id}`}
		/>
	}

	var onFormChange = bindFormChange(coupon, setCoupon);

	return <>
		{!!coupon?.deleted && <DeletedBanner date_deleted={coupon.date_deleted} />}
		<Groupper
			title={IS_CREATE ? 'Nuevo cupón' : 'Editar cupón'}
			style={{ maxWidth: 600, margin: 'auto' }}
			actions={!coupon.deleted && (
				<Button text="Guardar" color="black" onClick={submit} />
			)}
		>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
				<Input className='fluid' label="Codigo" placeholder="Codigo" value={coupon.coupon_code} onChange={onFormChange('coupon_code')} />
				<Field label='Activo' style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', width: 150, marginBottom: 0 }}>
					<Checkbox checked={!!coupon.active} onChange={onFormChange('active')} />
				</Field>
			</div>
			<Input label='Descripción' value={coupon.description} onChange={onFormChange('description')} textarea />
			<Field amount={is_x ? 3 : 2}>
				<Dropdown
					value={coupon.type_id}
					onValueChange={onFormChange('type_id')}
					label='Tipo de cupón'
					items={COUPON_TYPES.map(t=>({
						value: t.id,
						text: t.name
					}))}
				/>
				<Input inputType="number" label="Cantidad" value={coupon.param1} onChange={onFormChange('param1')} />
				{is_x && (
					<Input label="Cantidad" inputType="number" value={coupon.param2} onChange={onFormChange('param2')} style={{ width: 'auto' }} />
				)}
			</Field>
			<Field amount={coupon.limit_type===null ? 1 : 2} style={{ marginBottom: 0 }}>
				<Dropdown
					value={coupon.limit_type===null ? -1 : coupon.limit_type}
					onValueChange={onFormChange('limit_type')}
					label={'Limite de usos'}
					placeholder='Tipo de limite'
					items={COUPON_LIMIT_TYPES.map(t=>({
						value: t.id===-1 ? null : t.id,
						text: t.name
					}))}
				/>
				{coupon.limit_type!==null && (
					<Input inputType={'number'} label={'Cantidad'} value={coupon.limit_amount} onChange={onFormChange('limit_amount')} />
				)}
			</Field>

			{!!coupon.type_id && !!coupon.param1 && (
				<Message type='info' style={{ margin: 'auto', marginBottom: 10, marginTop: 10, maxWidth: 400 }} centered>
					Este cupón hara el siguiente descuento: <br />
					<b>
						{coupon.type_id==1 ? (
							`Se descontará $${addCommas(coupon.param1)} MXN del precio del boleto.`
						) : coupon.type_id==2 ? (
							`Se descontará ${addCommas(coupon.param1, false)}% del precio del boleto.`
						) : coupon.type_id==10 ? (
							<div>
								Se hará una promoción del {coupon.param1}x{coupon.param2}
								<br />
								<span style={{ fontWeight: 'normal', color: 'gray' }}>({coupon.param1} boleto{coupon.param1==1 ? '' : 's'} al precio de {coupon.param2} boleto{coupon.param2==1 ? '' : 's'})</span>
							</div>
						) : coupon.type_id==11 ? (
							`Se hará una promoción de ${coupon.param1} boletos a $${coupon.param2} MXN`
						) : null}
					</b>
				</Message>
			)}

			<Field amount={2} style={{ marginTop: 10 }}>
				<Input label="Fecha Inicio" value={coupon.date_start} calendar={{
					date: coupon.date_start ? fromUnix(coupon.date_start).toJSDate() : null,
					mode: 'date',
					onSelected: v=>onFormChange('date_start')(v.getTime()/1000),
				}}/>
				<Input label="Fecha Fin" value={coupon.date_end} calendar={{
					date: coupon.date_end ? fromUnix(coupon.date_end).toJSDate() : null,
					mode: 'date',
					onSelected: v=>onFormChange('date_end')(v.getTime()/1000),
				}} />
			</Field>
			<div style={{ marginTop: 15, marginBottom: 15, display: 'flex', flexDirection: 'column', gap: 10 }}>
				<Checkbox label={'Activo para todo el evento'} checked={!!coupon.all_event} onChange={onFormChange('all_event')} />
				<Checkbox label={'Activo para todas las secciones'} checked={!!coupon.all_sections} onChange={onFormChange('all_sections')} />
			</div>
			
			<Table fitted style={{ marginTop: 15 }} last={!!coupon.all_sections}>
				<Table.Head
					title='Fechas' 
					data={[['ID', 'Fecha', 'Ciudad', <Icon name='remove' />]]} 
					collapsingIndexes={[0, 2, 3, 4]}
					centeredIndexes={[4]}
					titleRight={!coupon.all_event && (
						<Button text='Agregar' color='black' size='tiny' iconName='plus-circle' onClick={bindClick(setShownModal, Modals.SELECT_DATE)} />
					)}
				/>
				<Table.Body>
					{coupon.all_event ? (
						<Table.Cell row colSpan={100}>
							<Message type='info' centered>
								El cupón está activo para <b>todo el evento</b>
							</Message>
						</Table.Cell>
					) : selectedDates.length==0 ? (
						<Table.Cell row colSpan={100}>
							<Message type='error' centered>
								No se ha seleccionado ningun calendario para este cupón
							</Message>
						</Table.Cell>
					) : selectedDates.map(e=>(
						<Table.Row key={`CPNDT-${e.date_id}`} collapsingIndexes={[0, 2, 3, 4]} data={[
							e.date_id,
							fromUnix(e.date).toFormat('dd/MM/yy HH:mm'),
							e.city,
							<Button iconName='remove' color='black' size='tiny' style={{ minWidth: 40 }} onClick={removeDate(e.date_id)} />
						]} />
					))}
				</Table.Body>
			</Table>
			{!coupon.all_sections && (
				<Table fitted last>
					<Table.Head
						title='Precios'
						collapsingIndexes={[0, 1, 3]}
						centeredIndexes={[4]}
						titleRight={
							<Button text='Agregar' color='black' size='tiny' iconName='plus-circle' onClick={bindClick(setShownModal, Modals.SELECT_SECTION)} />
						}
					/>
					<Table.Body>	
						{selectedPrices.length==0 ? (
							<Table.Cell row colSpan={100}>
								<Message type='error' centered>
									No se ha seleccionado ningún precio para este cupón
								</Message>
							</Table.Cell>
						) : selected_prices.map(d=>{
							return <React.Fragment key={`SLCPRDT-${d.date_id}`}>
								<Table.Row header>
									<Table.Cell colSpan={100}>
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<div className="date">{fromUnix(d.date).toFormat('dd/MMM/yy HH:mm')}</div>
											<div className="date">ID: {d.date_id}</div>
										</div>
									</Table.Cell>
								</Table.Row>
								{d.prices.map(a=>(
									<Table.Row key={`TBLPR-${a.price_id}`}>
										<Table.Cell collapsing>{a.price_id}</Table.Cell>
										<Table.Cell collapsing>{a.section_name}</Table.Cell>
										<Table.Cell>{a.price_name}</Table.Cell>
										<Table.Cell collapsing>
											<Button size='tiny' icon iconName='times' onClick={removePrice(a.price_id)} />
										</Table.Cell>
									</Table.Row>
								))}
							</React.Fragment>
						})}
					</Table.Body>
				</Table>
			)}
			<Message type='error' list={errorPrompts} style={{ marginTop: 15 }} />
		</Groupper>
		{!IS_CREATE && !!coupon && (
			<UserLog user={coupon} />
		)}

		{!coupon?.deleted && !IS_CREATE && (
			<Button text='Eliminar cupón' basic color='red' style={{ display: 'block', margin: 'auto', marginTop: 15, width: 200 }} onClick={bindClick(setShownModal, Modals.DELETE)} />
		)}
		{hasAccess(UserAccess.CATALOGS.COUPONS_DELETE) && (
			<Modal open={shownModal===Modals.DELETE} onClose={bindClick(setShownModal)} size='mini'>
				<Modal.Header text='Eliminar cupón' />
				<Modal.Content>
					<Header text={'¿Eliminar cupón?'} />
					<Message type='info'>
						Esto eliminará el cupón del sistema y ya no podrá ser usado. Cualquier orden que haya usado el cupón seguirá teniendo el descuento.
					</Message>
					<Message type='error' list={modalPrompts ? [modalPrompts] : []} />
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					<Button text='Eliminar' color='red' onClick={deleteCoupon} />
				</Modal.Actions>
			</Modal>
		)}
		<SearchDateModal
			open={shownModal===Modals.SELECT_DATE}
			title='Seleccionar fecha'
			selected={selectedDates.map(a=>a.date_id)}
			eventId={props.event.event_id}
			onClose={bindClick(setShownModal)}
			onSelected={onDateSelected}
		/>
		<Modal open={shownModal===Modals.SELECT_SECTION} size='tiny' onClose={bindClick(setShownModal)}>
			<Modal.Header>{selected_section ? `${selected_section.venue_name} - ${selected_section.section_name}` : 'Seleccionar sección'}</Modal.Header>
			<Modal.Content>
				{!eventSections ? (
					<Header loading text={'Cargando datos'} />
				) : !selectedSection ? (
					<Table fitted striped last top>
						<Table.Head
							data={[['ID', 'Recinto', 'Sección']]} 
							collapsingIndexes={[0, 1]}
						/>
						<Table.Body>
							{eventSections.length==0 && (
								<Table.Cell colSpan={100} row empty value={'No hay secciones disponibles'} />
							)}
							{eventSections.map((a, i, arr)=>{
								return <React.Fragment key={`SCT-${a.section_id}`}>
									{i>0 && arr[i-1].venue_id!==a.venue_id && (
										<Table.Divider />
									)}
									<Table.Row onClick={bindClick(setSelectedSection, a.section_id)} selectable collapsingIndexes={[0, 1]} data={[
										a.section_id,
										a.venue_name,
										a.section_name
									]} />
								</React.Fragment>
							})}
						</Table.Body>
					</Table>
				) : (
					!section_prices ? (
						<Header loading text={'Cargando datos'} />
					) : (
						<Table fitted last top>
							<Table.Body>
								{section_prices.length==0 && (
									<Table.Cell colSpan={100} row empty value={'No hay precios disponibles'} />
								)}
								{section_prices.map(d=>{
									return <React.Fragment key={`SCTPRDT-${d.date_id}`}>
										<Table.Row header>
											<Table.Cell colSpan={100}>
												<div style={{ display: 'flex', justifyContent: 'space-between' }}>
													<div>{fromUnix(d.date).toFormat('dd/MMM/yy HH:mm')} - {d.venue_name}</div>
													<div>ID: {d.date_id}</div>
												</div>
											</Table.Cell>
										</Table.Row>
										{d.prices.map(a=>{
											var selected = selectedPrices.findIndex(b=>b.price_id===a.price_id)>-1;
											return <Table.Row key={`SCPR-${a.price_id}`} onClick={onPriceSelected(a)} selectable collapsingIndexes={[0, 1]} data={[
												<Icon name={!selected ? 'circle' : 'circle-check'} solid={!!selected} />,
												a.price_id,
												a.price_name,
												`$${addCommas(a.cost+a.commission)}`
											]} />
										})}
									</React.Fragment>
								})}
							</Table.Body>
					</Table>
					)
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
			</Modal.Actions>
		</Modal>
	</>
}

export default CouponEdit;
